/**
 * English-only keyword catalogues for the meeting notes automation discovery nudge.
 *
 * Forked from `@confluence/experiment-meeting-signal-core`, which defines the
 * same keywords with `defineMessages` so they can be localized. That package is
 * shared with the Loom meeting-signal experiment (and `content-renderer` /
 * `editor-features`), so it keeps its localized catalogues and is deliberately
 * left untouched.
 *
 * The nudge is English-only: it opens an English Rovo conversation that builds
 * an English automation rule, so a localized trigger would surface a nudge whose
 * entire downstream experience is still English. These are matching inputs, not
 * user-facing copy, so they are plain constants and are not extracted for
 * translation.
 *
 * Matched whole-word and case-insensitively - see `matchMeetingSignal`. Frozen
 * so the compiled-regex cache in `matchMeetingSignal` (keyed on array identity)
 * stays warm across renders.
 */

export const MEETING_NOTES_TITLE_KEYWORDS: readonly string[] = Object.freeze([
	'meeting',
	'meetings',
	'sync',
	'standup',
	'daily standup',
	'huddle',
	'check-in',
	'1:1',
	'kickoff',
	'review',
	'demo',
	'showcase',
	'working session',
	'office hours',
	'town hall',
	'all hands',
	'leadership sync',
	'staff meeting',
	'team meeting',
	'customer call',
	'discovery call',
	'design review',
	'architecture review',
	'backlog refinement',
	'agenda',
	'meeting agenda',
	'meeting notes',
	'discussion topics',
	'discussion',
	'decisions',
	'decision log',
	'attendees',
	'participants',
	'outcomes',
	'parking lot',
]);

/**
 * Body-content keywords. `'meeting notes'` is matched against the body as well
 * as the title; it is already present in `MEETING_NOTES_TITLE_KEYWORDS`.
 */
export const MEETING_NOTES_CONTENT_KEYWORDS: readonly string[] = Object.freeze([
	"let's sync",
	"let's discuss",
	"let's review",
	'schedule a meeting',
	'schedule a call',
	'book time',
	'find time',
	'calendar invite',
	'invite',
	'join the meeting',
	'join the call',
	'Zoom',
	'Google Meet',
	'Microsoft Teams',
	'Webex',
	'recap',
	'meeting summary',
	'summary',
	'as discussed',
	'follow up',
	'follow-up',
	'decisions made',
	'takeaways',
	'owners',
	'discussion notes',
	'meeting notes',
]);
